// The help beside the Values, Fields and Subforms tabs, and the Values pane
// that shows it.
//
// WHY THIS EXISTS
//
// Types and Values are the templating layer of this service, and the four
// screens that carry them — a site, a page, a section and a `code` element —
// present them as two bare forms with nothing on either saying what the other
// is for. The Values tab of a fresh site said "No types defined." and stopped,
// which tells an author what is missing and not where to go or why they would
// want to. And the answer to "why" is different on each of the four screens:
// the same `{{site.values.brand}}` is read by every page on a site and by one
// page on a page, and a section's template reads `values.heading` where a
// page's shell reads nothing at all.
//
// So the words live here, once, keyed by the screen — `site`, `page`,
// `section`, `element` — and the four screens pass the key. The two shared
// forms in @repoflow/components take the paragraphs as nodes and know nothing
// of pages or sections, which is the right way round: what a field IS is the
// same everywhere, where its value is read is this app's business.
//
// Every expression quoted below is one the renderer honours, and nothing
// else: `<%= %>` and `<%- %>` and `for…of` are the grammar in
// common/render/engine, `{{site.values.x}}` is the interpolation pass in
// common/render/template, and `data-edit` is the inline-editing hook in
// common/render/template. A page's values are read as `site.values` because
// that is where they land — common/render/page merges a page's `values` over
// its site's before the context is built, and the context has no
// `page.values`. The help says so rather than inventing a nicer name.
import React from "react";
import Help from "@repoflow/components/help";
import { Form as ValuesForm } from "@repoflow/components/types-values";

// Where the values of each screen are read, as a sentence and a few
// expressions an author can paste.
const CONTEXTS = {
  site: {
    owner: "this site",
    reads:
      "A site's values are read by every page of the site — a brand name, a support address, an accent colour, anything that should say the same thing everywhere.",
    examples: [
      {
        use: "In the document fields on the Details tab — Document title, Description, Head content, Site styles",
        code: "{{site.values.brand}}",
      },
      {
        use: "In the HTML of any section or code element on the site",
        code: "{{site.values.brand}}",
      },
      {
        use: "The same, from inside a template expression",
        code: "<%= cxt.site.values.brand %>",
      },
      {
        use: "A page can override one of these values on its own Types and Values tabs, by declaring a field with the same name",
      },
    ],
  },
  page: {
    owner: "this page",
    reads:
      "A page's values are read on this page only. They sit on top of the site's: a field with the same name as one of the site's overrides that value here, a new name adds one. They are read with the same expression as the site's.",
    examples: [
      {
        use: "In the fields on the Configuration tab — Document title, Description, Head content",
        code: "{{site.values.headline}}",
      },
      {
        use: "In a section this page includes, so one shared section reads differently on each page",
        code: "{{site.values.headline}}",
      },
      {
        use: "In a code element's HTML",
        code: "<%= cxt.site.values.headline %>",
      },
    ],
  },
  section: {
    owner: "this section",
    reads:
      "A section's values are filled in here, once, and every page that includes the section shows the same content. The HTML, Style and Script on the Code tab read them by field name.",
    examples: [
      { use: "A text field, escaped", code: "<%= values.heading %>" },
      {
        use: "A paragraph field, which is already HTML",
        code: "<%- values.body %>",
      },
      {
        use: "A list field, one row at a time",
        code: "<% for (const item of values.items) { %><li><%= item.title %></li><% } %>",
      },
      { use: "A colour, in Style", code: "color: <%= values.accent %>;" },
      {
        use: "Something the whole site shares, from the site's own values",
        code: "{{site.values.brand}}",
      },
    ],
  },
  element: {
    owner: "this element",
    reads:
      "A code element's values belong to this page, and each language of the page keeps its own copy. The HTML, Style and Script on the Code tab read them by field name.",
    examples: [
      { use: "A text field, escaped", code: "<%= values.heading %>" },
      {
        use: "A paragraph field, which is already HTML",
        code: "<%- values.body %>",
      },
      {
        use: "A list field, one row at a time",
        code: "<% for (const item of values.items) { %><li><%= item.title %></li><% } %>",
      },
      {
        use: "Edit a value in place from the preview, by naming it on the tag that shows it",
        code: '<h1 data-edit="heading"><%= values.heading %></h1>',
      },
      {
        use: "Something the whole site shares, from the site's own values",
        code: "{{site.values.brand}}",
      },
    ],
  },
};

const contextOf = (context) => {
  const found = CONTEXTS[context];

  if (!found) {
    throw new Error(`types-help.context.unknown:${context}`);
  }

  return found;
};

// The sentence and the list, shared by the help paragraph and the empty state
// so the two never drift.
const Where = ({ context }) => {
  const { reads, examples } = contextOf(context);

  return (
    <>
      <p className="mb-2">{reads}</p>
      <ul className="mb-0 pl-3">
        {examples.map(({ use, code }, idx) => (
          <li key={idx}>
            {use}
            {code ? (
              <>
                : <code>{code}</code>
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </>
  );
};

export const ValuesHelp = ({ context }) => (
  <Help label="What are values?">
    <p className="mb-2">
      Values are the content filled into the fields declared on the Types tab:
      one control per field, in the order the fields were declared. Nothing here
      is markup — a template reads a value by its field's name — so{" "}
      {contextOf(context).owner} can be filled in by someone who never sees its
      code.
    </p>
    <Where context={context} />
  </Help>
);

// What the Values tab shows when there is no type to compile a form from.
export const NoTypes = ({ context }) => (
  <div className="text-muted small">
    <p className="mb-2">
      <b>No fields yet.</b> Values are the content filled into the fields that
      types declare, so add a field on the Types tab first — a label, a name and
      a type. Once there is one, this tab shows a control for it.
    </p>
    <Where context={context} />
  </div>
);

const FieldsHelp = ({ context }) => (
  <Help label="What are fields?">
    <p className="mb-2">
      A field is one slot of editable content on {contextOf(context).owner}.
      Give it a label — its name, which is what a template reads it by, fills in
      from the label — a type, and whatever that type needs: the choices of an
      options field, the bounds of a number, the default picture of an image.
      Each field becomes one control on the Values tab. A hidden field is
      declared but not shown there; a required one has to be filled in before
      saving.
    </p>
    <p className="mb-0">
      Text, paragraph, options, number, checkbox, color, date, icon and image
      each hold one value. List and object hold structured content and point at
      a subform, declared on the Subforms tab.
      {context === "page"
        ? " A page's fields are merged with its site's by name: a field with the same name as one of the site's overrides it on this page."
        : null}
    </p>
  </Help>
);

const SubformsHelp = () => (
  <Help label="What are subforms?">
    <p className="mb-2">
      A subform is a type of its own for structured or repeated content — a card
      with a title, a picture and a link, say — declared here with a name and
      fields of its own. On its own it shows nothing on the Values tab: go back
      to the Fields tab, add a field of type <code>list</code> (for many) or{" "}
      <code>object</code> (for one), and point it at the subform by name. The
      Values tab then shows the subform's fields inside that field, once per
      row.
    </p>
    <p className="mb-0">
      A subform can be referenced by any number of fields, and by fields of
      other subforms. The fields on the first tab are themselves the one type
      the template resolves against, named <code>root</code>.
    </p>
  </Help>
);

// One node per tab, built once per screen rather than per render: the two
// forms in types-form receive these as children of a tab body, and a node that
// is new on every keystroke is one more thing re-mounted under a form whose
// `config` this app already takes care to keep stable.
const TYPES_HELP = Object.fromEntries(
  Object.keys(CONTEXTS).map((context) => [
    context,
    {
      fields: <FieldsHelp context={context} />,
      subforms: <SubformsHelp />,
    },
  ]),
);

export const typesHelp = (context) => {
  contextOf(context);
  return TYPES_HELP[context];
};

// The Values tab of one of the four screens: the help, then the form, or the
// empty state when there is no type to build a form from.
export const ValuesPane = ({ context, ...props }) => (
  <>
    <ValuesHelp context={context} />
    <ValuesForm {...props} empty={<NoTypes context={context} />} />
  </>
);
